import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE, clearPortalStorage, hqFetch } from "../utils/api";
import { loginPathForRole } from "../utils/loginPaths";
import {
  persistOrgLogo,
  resolveMediaUrl,
  resolvePartnerLogoSrc,
  resolveStoredOrgLogo,
} from "../utils/mediaUrl";
import { fetchOrganizationLogo } from "../utils/orgLogoLoader";
import { unwrapUserPayload } from "../../admin-shared/unwrapUserPayload";
import {
  applyOrgEntitlementsPayload,
  refreshOrgEntitlementsFromApi,
} from "../../admin-shared/syncOrgEntitlements";

const UserContext = createContext();
export const useUser = () => useContext(UserContext);

const isPublicLoginPath = (pathname) => {
  if (pathname === "/nyafaz-ep/login" || pathname === "/nyafaz-ep/login/") return true;
  if (pathname === "/nyafaz-ep/partner" || pathname === "/nyafaz-ep/partner/") return true;
  if (pathname.includes("/set-password")) return true;
  return false;
};

const applyOrgLogo = (logo) => {
  const resolved = persistOrgLogo(logo) || resolveMediaUrl(logo);
  return resolved || null;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const loadedUserIdRef = useRef(null);

  useEffect(() => {
    if (isPublicLoginPath(location.pathname)) {
      setLoading(false);
      return;
    }

    const userId = localStorage.getItem("naturals_partner_userId");
    const role = localStorage.getItem("naturals_partner_role");

    if (!userId) {
      setLoading(false);
      loadedUserIdRef.current = null;
      navigate(loginPathForRole(role));
      return;
    }

    if (loadedUserIdRef.current === String(userId)) {
      setLoading(false);
      return;
    }

    setToken(null);

    let cancelled = false;
    const loadUser = async () => {
      try {
        const res = await hqFetch(`${API_BASE}/user/${userId}`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            clearPortalStorage();
            loadedUserIdRef.current = null;
            if (!cancelled) navigate(loginPathForRole(role));
          }
          if (!cancelled) setLoading(false);
          return;
        }
        const payload = await res.json();
        let nextUser = unwrapUserPayload(payload) || {};
        const isFranchisePartner = role === "user";
        let logo =
          applyOrgLogo(
            nextUser?.franchise_logo || nextUser?.logo_url || nextUser?.org_logo,
          ) ||
          resolveStoredOrgLogo() ||
          null;

        try {
          const brandLogo = await fetchOrganizationLogo({
            isFranchise: isFranchisePartner,
          });
          if (brandLogo) {
            logo = applyOrgLogo(brandLogo) || brandLogo;
          }
        } catch (_) {
          // Branding is optional — never clear the session for logo failures.
        }

        if (isFranchisePartner) {
          logo = resolvePartnerLogoSrc(
            logo || nextUser?.franchise_logo || nextUser?.logo_url || nextUser?.org_logo,
          );
        }

        if (logo) {
          nextUser = { ...nextUser, org_logo: logo, franchise_logo: logo };
        }
        if (nextUser.admin_permissions && typeof nextUser.admin_permissions === "object") {
          localStorage.setItem(
            "naturals_partner_admin_permissions",
            JSON.stringify(nextUser.admin_permissions),
          );
        }
        applyOrgEntitlementsPayload("partner", nextUser);
        await refreshOrgEntitlementsFromApi({
          portal: "partner",
          apiBase: API_BASE,
          hqFetch,
        });
        if (cancelled) return;
        loadedUserIdRef.current = String(userId);
        setUser(nextUser);
        setLoading(false);
      } catch (_) {
        if (!cancelled) setLoading(false);
      }
    };

    loadUser();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (isPublicLoginPath(location.pathname)) return undefined;
    const userId = localStorage.getItem("naturals_partner_userId");
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refreshOrgEntitlementsFromApi({
        portal: "partner",
        apiBase: API_BASE,
        hqFetch,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return (
    <UserContext.Provider value={{ user, token, loading, setUser, setToken }}>
      {children}
    </UserContext.Provider>
  );
};
